import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseQueryOptions,
  keepPreviousData,
  useInfiniteQuery,
  UseMutationResult,
  UseMutationOptions,
} from '@tanstack/react-query';
import {
  getDoctorList,
  getDoctorReviews,
  getDoctorProfile,
  notifyOnlineDoctor,
  getOnlineDoctorsList,
  getDoctorServiceTarifs,
  getSuggestedDoctorList,
  GetDoctorActivityStatus,
  getOnlineSuggestedDoctorList,
} from './index';
import {
  DoctorProfileResponse,
  NotifyOnlineDoctorPayload,
  NotifyOnlineDoctorResponse,
  GetSuggestedDoctorsPayload,
  GetSuggestedDoctorsResponse,
  DoctorServiceTarifsDataModel,
  GetOnlineSuggestedDoctorsPayload,
  GetOnlineSuggestedDoctorsResponse,
  GetOnlineDoctorListPayloadModel,
  GetOnlineDoctorListResponseModel,
  GetDoctorListDataModel,
} from '@models/ocs/doctor';
import { toast } from 'sonner';

export const useGetDoctorList = (
  body: any,
  options?: Omit<UseQueryOptions<GetDoctorListDataModel, Error>, 'queryKey'>,
): UseQueryResult<GetDoctorListDataModel, Error> => {
  return useQuery<GetDoctorListDataModel, Error>({
    queryKey: ['getDoctorList', body],
    queryFn: () =>
      getDoctorList({
        ...body,
        pageNumber: body?.pageNumber ?? 1,
      }),
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const useGetPaginateOnlineDoctorsList = (
  body: GetOnlineDoctorListPayloadModel,
  options?: Omit<
    UseQueryOptions<GetOnlineDoctorListResponseModel, Error>,
    'queryKey'
  >,
): UseQueryResult<GetOnlineDoctorListResponseModel, Error> => {
  return useQuery<GetOnlineDoctorListResponseModel, Error>({
    queryKey: ['getOnlineDoctorsList', body],
    queryFn: () => getOnlineDoctorsList(body),
    ...options,
  });
};

export const useGetOnlineDoctorsList = (
  body: any,
  options?: any,
  // options?: Omit<UseInfiniteQueryOptions<GetDocotrListResponse, Error>, 'queryKey'>
) => {
  const {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    queryKey: ['getOnlineDoctorsList', body],
    queryFn: ({ pageParam = 1 }) => {
      return getOnlineDoctorsList({
        ...body,
        pageNumber: (body?.pageNumber as number) ?? pageParam,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (data) => {
      return Math.floor(data?.totalCount / 10) + 1 === data?.pageNumber
        ? undefined
        : data?.pageNumber + 1;
    },
    ...options,
  });

  return {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  };
};

export const useGetSuggestedDoctorList = (
  body: GetSuggestedDoctorsPayload,
  options?: Omit<
    UseQueryOptions<GetSuggestedDoctorsResponse, Error>,
    'queryKey'
  >,
): UseQueryResult<GetSuggestedDoctorsResponse, Error> => {
  return useQuery<GetSuggestedDoctorsResponse, Error>({
    queryKey: ['getSuggestedDoctorList', body],
    queryFn: () => getSuggestedDoctorList(body),
    enabled: !!body.doctorId && !!body.specialityId,
    ...options,
  });
};

export const useGetOnlineSuggestedDoctorList = (
  body: GetOnlineSuggestedDoctorsPayload,
  options?: Omit<
    UseQueryOptions<GetOnlineSuggestedDoctorsResponse, Error>,
    'queryKey'
  >,
): UseQueryResult<GetOnlineSuggestedDoctorsResponse, Error> => {
  return useQuery<GetOnlineSuggestedDoctorsResponse, Error>({
    queryKey: ['getOnlineSuggestedDoctorList', body],
    queryFn: () => getOnlineSuggestedDoctorList(body),
    enabled: !!body.doctorId && !!body.specialityId,
    ...options,
  });
};

export const useGetDoctorProfile = (
  id: number,
  options?: Omit<UseQueryOptions<DoctorProfileResponse, Error>, 'queryKey'>,
): UseQueryResult<DoctorProfileResponse, Error> => {
  return useQuery<DoctorProfileResponse, Error>({
    queryKey: ['getDoctorProfile', id],
    queryFn: () => getDoctorProfile(id),
    refetchOnMount: true,
    enabled: !!id,
    ...options,
  });
};

export const useGetDoctorServiceTarifs = (
  id: number,
  specialityId: number,
  systemType: number,
  hasSchedule: boolean,
  options?: Omit<
    UseQueryOptions<DoctorServiceTarifsDataModel, Error>,
    'queryKey'
  >,
): UseQueryResult<DoctorServiceTarifsDataModel, Error> => {
  return useQuery<DoctorServiceTarifsDataModel, Error>({
    queryKey: [
      'getDoctorServiceTarifs',
      id,
      specialityId,
      systemType,
      hasSchedule,
    ],
    queryFn: () =>
      getDoctorServiceTarifs(id, specialityId, systemType, hasSchedule),
    refetchOnMount: true,
    ...options,
  });
};

export const useGetDoctorReviewsInfinite = (body: any, options?: any) => {
  const {
    data,
    isLoading,
    isSuccess,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    queryKey: ['getDoctorReviews', body],
    queryFn: ({ pageParam = 1 }) => {
      return getDoctorReviews({
        ...body,
        pageNumber: (body?.pageNumber as number) ?? pageParam,
        pageSize: 5,
      });
    },
    initialPageParam: 1,
    getNextPageParam: (data) => {
      return Math.floor(data?.totalCount / 5) + 1 === data?.pageNumber
        ? undefined
        : data?.pageNumber + 1;
    },
    ...options,
  });
  return {
    data,
    isLoading,
    isSuccess,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  };
};

export const useGetDoctorActivityStatus = (
  id: number,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey'>,
): UseQueryResult<any, Error> => {
  return useQuery<any, Error>({
    queryKey: ['GetDoctorActivityStatus', id],
    queryFn: () => GetDoctorActivityStatus(id),
    enabled: !!id,
    ...options,
  });
};

export const useNotifyOnlineDoctor = (
  options?: UseMutationOptions<
    NotifyOnlineDoctorResponse,
    Error,
    NotifyOnlineDoctorPayload
  >,
): UseMutationResult<
  NotifyOnlineDoctorResponse,
  Error,
  NotifyOnlineDoctorPayload
> => {
  return useMutation({
    mutationFn: notifyOnlineDoctor,
    onSuccess() {
      toast.success(
        'درخواست اطلاع رسانی جهت آنلاین شدن دکتر با موفقیت ثبت شد.',
      );
    },
    onError: (err: Error) => {
      toast.error(err?.message);
    },
    ...options,
  });
};
